import _ from 'lodash';
import React, { useState, Fragment } from 'react';
import { Link } from 'gatsby';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import { generateImageCdnPath, ImageSize } from '../utils/images';
import { AcornProduct, PlantType } from '../generated/types-and-hooks';
import { useHydratePricesAndStock } from '../utils/public-api';
import SortBySelect from './SortBySelect';
import Loading from './Loading';

const PRODUCT_TYPE_NAMES = {
  PLANT: '3-inch pot',
  COLLECTION: 'collection',
};

const getProductLink = (product: AcornProduct) => {
  return `/organic-plants/${product.Slug}/`;
};

export const Product = (
  DisableFacets: boolean,
  product: AcornProduct,
  index: number,
  LinkToPlugtray: boolean,
): JSX.Element => {
  const {
    Id,
    BotanicalName,
    CommonName,
    Name,
    Price,
    PricePlugtrays,
    InStock,
    InStockPlugtrays,
    Slug,
    ImageHashes,
    Type,
    DisplayMVG,
    DisplayPlugtrays,
    BackorderLimit,
  } = product;

  const imageUrl = generateImageCdnPath(
    _.get(ImageHashes, 0),
    ImageSize.Medium,
  );

  let renderedStock, renderedStockPlugtrays, potStockClass, plugtrayStockClass;
  if (InStock === 0 && BackorderLimit === 0 && DisplayMVG) {
    renderedStock = (
      <p className="product-thumb--link__notify">
        Out of stock in pots &middot; Notify me
      </p>
    );
    potStockClass = 'product-thumb--price__out-of-stock';
  }
  if (InStockPlugtrays === 0 && DisplayPlugtrays) {
    renderedStockPlugtrays = (
      <p className="product-thumb--link__notify">
        Out of stock in plug trays &middot; Notify me
      </p>
    );
    plugtrayStockClass = 'product-thumb--price__out-of-stock';
  }

  const linkTarget = LinkToPlugtray
    ? `${getProductLink(product)}?type=${PlantType.Plugtray}`
    : getProductLink(product);

  return (
    <div
      key={`${index}-${Id}`}
      className={`${
        DisableFacets == true
          ? 'cell small-12 medium-6 large-3 product-thumb--card'
          : 'cell small-12 medium-6 large-4 product-thumb--card'
      }`}
    >
      <Link to={linkTarget} className="product-thumb--link__product">
        <div
          className="product-thumb--image"
          style={{
            backgroundImage: `url("${imageUrl}")`,
          }}
        />
        {BotanicalName ? (
          <p className="product-thumb--title">
            <em>{BotanicalName}</em> <br />
            <strong>{CommonName}</strong>
          </p>
        ) : (
          <p className="product-thumb--title">
            <strong>{Name}</strong>
          </p>
        )}
        <div>
          {DisplayMVG && (
            <>
              <p className={`product-thumb---price ${potStockClass}`}>
                <span>$</span>
                <span>{Price}</span> / {PRODUCT_TYPE_NAMES[Type]}
              </p>
              {renderedStock}
            </>
          )}
          {DisplayPlugtrays && (
            <>
              <p className={`product-thumb---price ${plugtrayStockClass}`}>
                <span>$</span>
                <span>{PricePlugtrays}</span> / 128-plant plug tray
              </p>
              {renderedStockPlugtrays}
            </>
          )}
        </div>
      </Link>
    </div>
  );
};

type TagFacetConfig = {
  Prefix: string;
  Name: string;
  MutuallyExclusive?: boolean;
  getValue?: Function;
};

const TAG_FACETS: TagFacetConfig[] = [
  {
    Prefix: 'use',
    Name: 'Uses',
  },
  {
    Prefix: 'char',
    Name: 'Characteristics',
  },
  {
    Prefix: 'zone',
    Name: 'Zone',
    MutuallyExclusive: true,
    getValue: (tag: RenderTagInput) => Number(tag.TagValue),
  },
];

const frequencies = (values: string[]) => _.countBy(values, _.identity);

const calculateFacets = (products: AcornProduct[]) =>
  frequencies(_.flatMap(products, ({ Tags }) => Tags) as string[]);

type SetSelectedCallable = (
  Tag: string,
  mutuallyExclusivePrefix?: string,
) => void;
type TagFacetInput = {
  Prefix: string;
  Name: string;
  Frequencies: object;
  SelectedFacets: object;
  SetSelected: SetSelectedCallable;
  MutuallyExclusive?: boolean;
  Index: number;
  getValue?: Function;
};

type RenderTagInput = {
  Tag: string;
  TagValue: string;
  Count: number;
};
const TagFacet = ({
  Prefix,
  Name,
  Frequencies,
  SelectedFacets,
  SetSelected,
  MutuallyExclusive,
  Index,
  getValue,
}: TagFacetInput): JSX.Element => {
  const tags = _.filter(
    _.map(Frequencies, (Count, tag) => {
      if (tag.startsWith(Prefix)) {
        const [prefix, TagValue] = tag.split(':');
        return { Count, TagValue, Tag: tag };
      }
    }),
    _.identity,
  );

  const renderTag = (
    { Count, TagValue, Tag }: RenderTagInput,
    index: number,
  ): JSX.Element => {
    const inputType = MutuallyExclusive ? 'radio' : 'checkbox';
    return (
      <div key={index}>
        <input
          type={inputType}
          name={MutuallyExclusive ? Prefix : Tag}
          value={Tag}
          id={Tag}
          onChange={(e) => {
            SetSelected(Tag, MutuallyExclusive ? Prefix : undefined);
          }}
          defaultChecked={_.has(SelectedFacets, Tag)}
        />
        <label htmlFor={Tag}>
          {_.startCase(_.toLower(TagValue))} ({Count})
        </label>
      </div>
    );
  };
  return (
    <Fragment key={Index}>
      {tags.length > 0 ? (
        <fieldset>
          <legend>{Name}</legend>
          {_.map(_.sortBy(tags, getValue || 'TagValue'), renderTag)}
        </fieldset>
      ) : null}
    </Fragment>
  );
};

type ProductGridArgs = {
  staticProducts?: AcornProduct[];
  maxProducts?: number;
  hydratedProducts?: AcornProduct[];
  DisableFacets?: boolean;
  DisableHeader?: boolean;
  SortBy?: string;
  LinkToPlugtray?: boolean;
};
export const ProductGrid = ({
  staticProducts,
  maxProducts,
  hydratedProducts,
  DisableFacets = false,
  DisableHeader = false,
  SortBy,
  LinkToPlugtray = false,
}: ProductGridArgs) => {
  const [selectedFacets, setSelectedFacets] = useState({});
  const [hideOutOfStock, setHideOutOfStock] = useState(false);
  const [sortBy, sortByRendered] = SortBySelect();

  let productsToRender: AcornProduct[];

  if (hydratedProducts === undefined && staticProducts !== undefined) {
    const hydratedProductsContext = useHydratePricesAndStock({
      Products: staticProducts,
    });
    productsToRender = hydratedProductsContext.data?.Products;
  }
  if (hydratedProducts) {
    productsToRender = hydratedProducts;
  }
  if (productsToRender! === undefined) {
    return <Loading />;
  }
  if (maxProducts !== undefined) {
    productsToRender = _.slice(
      _.orderBy(productsToRender, ['InStock'], ['desc']),
      0,
      maxProducts,
    );
  }
  productsToRender = _.sortBy(
    productsToRender,
    SortBy ? SortBy : sortBy,
  ) as AcornProduct[];

  const Frequencies = calculateFacets(productsToRender);

  const SetSelected = (tag: string, mutuallyExclusivePrefix?: string): void => {
    // Toggles tag selection
    const newFacets = { ...selectedFacets };
    if (_.has(selectedFacets, tag)) {
      _.unset(newFacets, tag);
    } else {
      if (mutuallyExclusivePrefix) {
        _.map(newFacets, (value, key) => {
          if (key.startsWith(mutuallyExclusivePrefix)) {
            _.unset(newFacets, key);
          }
        });
      }
      _.set(newFacets, tag, true);
    }
    setSelectedFacets(newFacets);
  };

  let renderdFacetSection;
  let actualProductsToRender = productsToRender;
  if (!DisableFacets) {
    const renderedFacets = _.map(
      TAG_FACETS,
      ({ Prefix, Name, MutuallyExclusive, getValue }, Index) =>
        TagFacet({
          Prefix,
          Name,
          Frequencies,
          SetSelected,
          SelectedFacets: selectedFacets,
          MutuallyExclusive,
          Index,
          getValue,
        }),
    );

    const selectedFacetsArray = _.map(selectedFacets, (value, facet) => facet);
    actualProductsToRender = _.filter(productsToRender, (product) => {
      // Apply facet filters
      if (_.isEmpty(selectedFacets)) {
        return true;
      } else {
        return _.every(selectedFacetsArray, (facet) =>
          product.Tags?.includes(facet),
        );
      }
    });
    renderdFacetSection = renderedFacets;
  }
  let hiddenOutOfStockCount = 0;
  if (hideOutOfStock) {
    const origCount = _.size(actualProductsToRender);
    actualProductsToRender = _.filter(
      actualProductsToRender,
      ({ InStock, InStockPlugtrays }) => InStock > 0 || InStockPlugtrays > 0,
    );
    hiddenOutOfStockCount = origCount - _.size(actualProductsToRender);
  }

  let renderedHeader;
  if (!DisableHeader) {
    const hiddenTextRendered =
      hiddenOutOfStockCount > 0 ? (
        <i>({hiddenOutOfStockCount} results hidden)</i>
      ) : null;
    renderedHeader = (
      <>
        <div className="cell small-12 medium-4">{sortByRendered}</div>
        <div className="cell small-12 medium-8">
          <label>
            <input
              type="checkbox"
              checked={hideOutOfStock}
              id="hide-out-of-stock"
              name="hideOutOfStock"
              onChange={() => setHideOutOfStock(!hideOutOfStock)}
            />
            Hide out of stock {hiddenTextRendered}
          </label>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="cell small-12">
        <div className="grid-x">
          {!DisableFacets && (
            <>
              <div className="product-grid--filters cell show-for-medium medium-4 large-3">
                {renderdFacetSection}
              </div>
            </>
          )}
          <div
            className={`cell ${
              DisableFacets ? 'small-12' : 'small-12 medium-8 large-9'
            }`}
          >
            <div className="grid-x grid-margin-x grid-margin-y">
              {renderedHeader}
              {!DisableFacets && (
                <div className="product-grid--filters cell small-12 show-for-small-only">
                  <Accordion allowZeroExpanded>
                    <AccordionItem>
                      <AccordionItemHeading>
                        <AccordionItemButton>
                          <p className="button small expanded primary no-span">
                            Filter products
                          </p>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel>
                        {renderdFacetSection}
                      </AccordionItemPanel>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}
              {_.map(actualProductsToRender, (product, index) =>
                Product(DisableFacets, product, index, LinkToPlugtray),
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductGrid;
